"use client"

import type React from "react"
import { useMemo } from "react"
import { View, StyleSheet } from "react-native"
import { Table } from "../../DataTable/Table"
import type { ColumnDefinition } from "../../../types/table"
import { getAllSpanProperties } from "../../../constants/spanProperties"
import { debugLog } from "../../../constants/debug"
import { getDefaultUnit, getMeasureType } from "../../../utils/unitUtils"
import { processNodeData, findNodesByName, getNodesByParent, getAllNodesOfType } from "../../../utils/assetNodeUtils"
import type { TabComponentProps } from "../../../types/tabs"

export const SpansTab: React.FC<TabComponentProps> = ({ selectedNode, projectData, menuAction }) => {
    // Generate column definitions from schema
    debugLog("SpansTab rendered with:", { selectedNode, projectData, menuAction })
    const columns = useMemo(() => {
        const spanSchema = projectData?.schemas?.Span
        if (!spanSchema) {
            debugLog("No Span schema found in projectData")
            return []
        }

        debugLog("Generating columns with schema:", spanSchema)

        // Get all property display names
        const allPropertyNames = getAllSpanProperties(spanSchema)

        // Map the display names to column definitions
        return allPropertyNames
            .map((displayName) => {
                // Find the schema entry that matches this display name
                const schemaEntries = Object.entries(spanSchema)
                const matchingSchemaEntry = schemaEntries.find(([_, field]) => field.title === displayName)

                if (!matchingSchemaEntry) {
                    debugLog(`No schema found for display name: ${displayName}`)
                    return null
                }

                const [schemaKey, field] = matchingSchemaEntry
                const defaultUnit = getDefaultUnit(field)

                const column = {
                    key: schemaKey, // Use the schema key for data lookup
                    title: displayName, // Use the display name for the column title
                    unit: defaultUnit,
                    measure: getMeasureType(defaultUnit),
                    sortable: true,
                    filterable: true,
                    align: "center" as const,
                    type: field.type,
                } as ColumnDefinition

                debugLog("Created column definition:", column)

                return column
            })
            .filter((col): col is ColumnDefinition => col !== null)
    }, [projectData])

    // Generate span data based on selection
    const spanData = useMemo(() => {
        debugLog("Generating span data with:", {
            selectedNode,
            hasProjectData: !!projectData,
            columnsLength: columns.length,
        })

        if (!projectData || !columns.length) return []

        // Handle span selection
        if (selectedNode?.type === "span") {
            debugLog("Processing span selection:", selectedNode.name)
            const spanData = findNodesByName(projectData, selectedNode.name, "span")
            debugLog("Found span data:", spanData)

            // Ensure we only get the exact span we want
            const exactSpanData = spanData.find((span) => span.name === selectedNode.name && span.type === "span")

            debugLog("Filtered to exact span:", exactSpanData)

            // Return array with single span or empty array if not found
            return exactSpanData ? [processNodeData(exactSpanData, columns)] : []
        }

        // Handle section selection
        if (selectedNode?.type === "section") {
            debugLog("Processing section selection:", selectedNode.name)
            const sectionSpans = getNodesByParent(projectData, selectedNode.name, "section", "span")
            debugLog("Found section spans:", sectionSpans)
            return sectionSpans.map((span) => processNodeData(span, columns))
        }

        // Handle no selection or line selection
        if (!selectedNode || selectedNode.type === "line") {
            debugLog("Processing all spans from all sections")
            const allSpans = getAllNodesOfType(projectData, "span")
            debugLog("Found all spans:", allSpans)
            return allSpans.map((span) => processNodeData(span, columns))
        }

        return []
    }, [selectedNode, projectData, columns])

    return (
        <View style={styles.container}>
            <Table
                columns={columns}
                data={spanData}
                title="Span Details"
                tableName="spans"
                pageSize={10}
                menuAction={menuAction}
            />
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
})

